import { type IMatchConfigEntry, type IRawTiledMap, isProceduralMapConfig, validateRawTiledMap } from '@nova/types';
import { buildStubTiledMap, type StubTiledMapOptions } from '@nova/utils';

import { downloadMapTiledJson } from './map-tiled-json';

export interface ResolveMatchMapJsonOptions {
  /** When set (e.g. folder import), used instead of config-driven resolution. */
  preloadedMapData?: IRawTiledMap;
  /** Dev maps base URL override for CDN maps (`playerDebugSettingsGES.devMapsAssetPath`). */
  devMapsAssetPath?: string;
  /**
   * Dimensions for {@link MapDataSource.Procedural} stubs.
   * Callers look these up from the procedural TF catalog (or equivalent) by map id.
   */
  proceduralStubOptions?: StubTiledMapOptions;
}

/**
 * Single map JSON resolution path for AGE hosts and the render engine.
 *
 * Precedence:
 * 1. `preloadedMapData` (debug / extension import)
 * 2. `matchConfig.mapDataSource === procedural` → {@link buildStubTiledMap}
 * 3. Remote or dev URL fetch via {@link downloadMapTiledJson}
 */
export async function resolveMatchMapJson(
  matchConfig: IMatchConfigEntry,
  options: ResolveMatchMapJsonOptions = {}
): Promise<IRawTiledMap> {
  if (options.preloadedMapData) {
    return validateRawTiledMap(structuredClone(options.preloadedMapData));
  }

  if (isProceduralMapConfig(matchConfig)) {
    return validateRawTiledMap(buildStubTiledMap(options.proceduralStubOptions));
  }

  return downloadMapTiledJson({
    assetName: matchConfig.mapAssetName,
    devMapsAssetPath: options.devMapsAssetPath
  });
}
